import { create } from "zustand"

import type { PriceApprovalSearchParams } from "../schemas"

import type { GetPriceApprovalListQuery, PriceChangeSubmission } from "@/graphql/codegen/graphql"
import { GetPriceApprovalListDocument, PriceChangeSubmissionStatus, PriceChangeType } from "@/graphql/codegen/graphql"
import { createSearchStore } from "@/stores/createSearchStore"

export type PriceApprovalSearchFilters = PriceApprovalSearchParams & { assignedToId?: string }

const deduplicatePriceApprovals = (
  newData: PriceChangeSubmission[],
  existingData: PriceChangeSubmission[]
): PriceChangeSubmission[] => {
  const uniqueIds = new Set(existingData.map((priceChangeSubmission) => priceChangeSubmission.id))
  return [...existingData, ...newData.filter((priceChangeSubmission) => !uniqueIds.has(priceChangeSubmission.id))]
}

export const usePriceApprovalSearchStore = create(
  createSearchStore<PriceChangeSubmission, PriceApprovalSearchFilters, GetPriceApprovalListQuery>({
    documentNode: GetPriceApprovalListDocument,
    deduplicateData: deduplicatePriceApprovals,
    getQueryInput: (filters: PriceApprovalSearchFilters, offset: number, limit: number) => {
      const { field, order, query, assignedToId } = filters
      return {
        filter: {
          changeType: PriceChangeType.SellingPrice,
          status: [PriceChangeSubmissionStatus.Submitted],
          assignedToIds: assignedToId ? [assignedToId] : undefined,
          search: query,
        },
        offset,
        limit,
        sort: { field, direction: order },
      }
    },
    getQueryVariables: (queryInput) => ({ input: queryInput }),
    extractDataFromResponse: (data) => ({
      __typename: data.priceChange.getPriceChangeHistory.__typename,
      data:
        data?.priceChange.getPriceChangeHistory.__typename === "GetPriceChangeHistorySuccess"
          ? (data.priceChange.getPriceChangeHistory.priceChangeSubmissions as PriceChangeSubmission[])
          : [],
      error:
        data?.priceChange.getPriceChangeHistory.__typename === "GetPriceChangeHistoryFailure"
          ? data.priceChange.getPriceChangeHistory.error
          : undefined,
    }),
  })
)
